import { useCallback, useMemo } from "react";
import { columnForAggregation, columnInfo } from "@vzbl/integrations";
import { asKey, eq } from "src/utils/fields";
import { SearchState, Condition, Field } from "../types";
import { ensureState, serializeParams } from "./params-state-reducer";
import { useIntegrationConfig } from "./useIntegrationConfig";

// This is where questions are asked about the current params state.
// callback versions, stable to use as props (I think)
export function useParamsStateSelectors() {
  const integrationConfig = useIntegrationConfig().data;
  const select = useMemo(
    () => selectors(integrationConfig),
    [integrationConfig]
  );
  return {
    countGroupBys: useCallback(
      (...args: Args<"countGroupBys">) => select.countGroupBys(...args),
      [select]
    ),
    countSelects: useCallback(
      (...args: Args<"countSelects">) => select.countSelects(...args),
      [select]
    ),
    countAggregations: useCallback(
      (...args: Args<"countAggregations">) => select.countAggregations(...args),
      [select]
    ),
    equalToState: useCallback(
      (...args: Args<"equalToState">) => select.equalToState(...args),
      [select]
    ),
    hasAggregation: useCallback(
      (...args: Args<"hasAggregation">) => select.hasAggregation(...args),
      [select]
    ),
    getColumnForAggregation: useCallback(
      (...args: Args<"getColumnForAggregation">) =>
        select.getColumnForAggregation(...args),
      [select]
    ),
    getAttributeFilter: useCallback(
      (...args: Args<"getAttributeFilter">) =>
        select.getAttributeFilter(...args),
      [select]
    ),
    getAttributeFilterOperator: useCallback(
      (...args: Args<"getAttributeFilterOperator">) =>
        select.getAttributeFilterOperator(...args),
      [select]
    ),
    getAttributeFilterValues: useCallback(
      (...args: Args<"getAttributeFilterValues">) =>
        select.getAttributeFilterValues(...args),
      [select]
    ),
    getFilters: useCallback(
      (...args: Args<"getFilters">) => select.getFilters(...args),
      [select]
    ),
    groupByAt: useCallback(
      (...args: Args<"groupByAt">) => select.groupByAt(...args),
      [select]
    ),
    hasAttributeFilter: useCallback(
      (...args: Args<"hasAttributeFilter">) =>
        select.hasAttributeFilter(...args),
      [select]
    ),
    hasAttributeFilterOperator: useCallback(
      (...args: Args<"hasAttributeFilterOperator">) =>
        select.hasAttributeFilterOperator(...args),
      [select]
    ),
    hasAttributeFilterValue: useCallback(
      (...args: Args<"hasAttributeFilterValue">) =>
        select.hasAttributeFilterValue(...args),
      [select]
    ),
    hasGroupBy: useCallback(
      (...args: Args<"hasGroupBy">) => select.hasGroupBy(...args),
      [select]
    ),
    hasGroupBys: useCallback(
      (...args: Args<"hasGroupBys">) => select.hasGroupBys(...args),
      [select]
    ),
    hasSelects: useCallback(
      (...args: Args<"hasSelects">) => select.hasSelects(...args),
      [select]
    ),
    isStartState: useCallback(
      (...args: Args<"isStartState">) => select.isStartState(...args),
      [select]
    ),
    isComparable: useCallback(
      (...args: Args<"isComparable">) => select.isComparable(...args),
      [select]
    ),
  };
}

// plain old functions here.
// when importing this function, isComparable will always return false unless you pass integrationConfig
export function selectors(
  integrationConfig?: ReturnType<typeof useIntegrationConfig>["data"]
) {
  const isStartState = (state: SearchState, orgId: string) =>
    serializeParams(state) === serializeParams(ensureState({ orgId }));
  const hasAttributeFilter = (state: SearchState, attribute: Field) => {
    if (!state.filters) return false;
    return state.filters.some((filter) => eq(filter, attribute));
  };
  const getAttributeFilter = (state: SearchState, attribute: Field) =>
    (state.filters || []).find((filter) => eq(filter, attribute))!;
  const getAttributeFilterOperator = (...args: [SearchState, Field]) =>
    Object.keys(getAttributeFilter(...args)?.comparison || {})[0]! as
      | "IN"
      | "NOT IN";
  const getAttributeFilterValues = (...args: [SearchState, Field]) =>
    Object.values(getAttributeFilter(...args)?.comparison || {})[0] ||
    ([] as string[]);
  const hasAttributeFilterValue = (
    state: SearchState,
    attribute: Field,
    value: string
  ) => getAttributeFilterValues(state, attribute).includes(value);
  const hasAttributeFilterOperator = (
    state: SearchState,
    attribute: Field,
    value: string
  ) => getAttributeFilterOperator(state, attribute) === value;
  const getFilters = (
    state: SearchState,
    fn?: (filter: Condition) => boolean
  ) => {
    if (!state.filters) return [];
    return state.filters.filter(fn || Boolean);
  };
  const countGroupBys = (state: SearchState) => (state.groupBys || []).length;
  const hasGroupBys = (state: SearchState) => countGroupBys(state) > 0;
  const hasGroupBy = (state: SearchState, attribute?: string) => {
    if (!state.groupBys || state.groupBys.length === 0) return false;
    return attribute
      ? state.groupBys.map((g) => g.attribute).includes(attribute)
      : true;
  };
  const countSelects = (state: SearchState) => (state.select || []).length;
  const hasSelects = (state: SearchState) => countSelects(state) > 0;
  const groupByAt = (state: SearchState, index: number) =>
    (state.groupBys || [])[index]!;
  const countAggregations = (state: SearchState) =>
    (state.aggregations || []).length;
  const hasAggregation = (state: SearchState) =>
    (state.aggregations?.length || 0) > 0;
  const getAggregations = (state: SearchState) => state.aggregations || [];
  const getColumnForAggregation = (
    _: SearchState,
    aggregation:
      | Exclude<SearchState["aggregations"], undefined>[number]
      | undefined
  ) =>
    columnForAggregation(
      aggregation || { attribute: "uuid", reducer: "count", target: "Activity" }
    );
  const isDated = (state: SearchState) => {
    let filter = getAttributeFilter(state, {
      target: "Activity",
      attribute: "occurredAt",
    });
    return (
      state.duration ||
      (state.startDate != undefined && state.endDate != undefined) ||
      (filter?.comparison && "WITHIN" in filter?.comparison)
    );
  };

  const isComparable = (state: SearchState) => {
    return (
      isDated(state) &&
      hasGroupBys(state) &&
      (state.aggregations || []).every(
        (agg) =>
          columnInfo(asKey(agg), integrationConfig as any)?.type === "number"
      )
    );
  };

  const equalToState = (state1: SearchState, state2: SearchState) => {
    return (
      serializeParams({ ...state1, orgId: "" }) ===
      serializeParams({ ...state2, orgId: "" })
    );
  };

  return {
    countAggregations,
    countGroupBys,
    countSelects,
    equalToState,
    getAggregations,
    getAttributeFilter,
    getAttributeFilterOperator,
    getAttributeFilterValues,
    getColumnForAggregation,
    getFilters,
    groupByAt,
    hasAggregation,
    hasAttributeFilter,
    hasAttributeFilterOperator,
    hasAttributeFilterValue,
    hasGroupBy,
    hasGroupBys,
    hasSelects,
    isComparable,
    isStartState,
  };
}

type Args<Name extends keyof ReturnType<typeof selectors>> = Parameters<
  ReturnType<typeof selectors>[Name]
>;
